'use strict';
import { Injectable } from '@angular/core';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { Observable, Subject, timer } from 'rxjs';
import { map, repeatWhen, switchMap, take, takeUntil } from 'rxjs/operators';
import { BULK_OPERATION_STATUS } from '../interfaces/bulk-operation.interface';

const NOT_NOTIFIED: string = 'NOT-NOTIFIED';

@Injectable()
export class BulkOperationPollingService {

  constructor(private _ispmHttpClient: IspmHttpClient,
              private _authService: AuthenticationService) {}

  private _worklistTemplateId: string;
  private _userId: string;
  private readonly _runPolling$ = new Subject<void>();
  private readonly _stopPolling$ = new Subject<void>();

  private getBulkOperations(): Observable<any> { 
    const param = {context: '%5B' + this._userId + ',' + this._worklistTemplateId + ',' + NOT_NOTIFIED + '%5D'};
    return this._ispmHttpClient.get('entity-tree/EtlFlowRequest', param);
  }

  public initPolling(pollingInterval: number): Observable<any> {
    //Polling for bulk operation of the current user and worklistTemplate
    this._userId = this._authService.user.id;

    return timer(0, pollingInterval)
      .pipe(
        takeUntil(this._stopPolling$),
        repeatWhen(() => this._runPolling$),
        switchMap(() => {
          return this.getBulkOperations().pipe(
              take(1),
              map((operationsData) => {
                //If there are no not-notified items, stop polling. Polling will be started after user creates a new bulk operation.
                if (Array.isArray(operationsData)){
                  if (operationsData.length === 0){
                    this.stopPolling();
                  }
                  else{
                    if (operationsData[0].status && (operationsData[0].status.value === BULK_OPERATION_STATUS.BULK_SUCCESS ||
                      operationsData[0].status.value === BULK_OPERATION_STATUS.BULK_FAILED)){
                      this.stopPolling();
                    }
                    if (operationsData[0].status.value === BULK_OPERATION_STATUS.BULK_SUCCESS){
                      this.updateSuccessStatus(operationsData[0]);
                    }
                    return operationsData;
                  }
                }
              })
            )
        })
      )
  }

  public startPolling(worklistTemplateId: string){
    this._worklistTemplateId = worklistTemplateId;
    this._runPolling$.next();
  } 

  public stopPolling(){
    this._stopPolling$.next();
  }

  private updateSuccessStatus(operationData: any){
    //The server returns success even if only part of the items were processed so need to parse the response history to understand if all were processed successfully or only part.
    if (operationData.status && operationData.status.value === BULK_OPERATION_STATUS.BULK_SUCCESS){
      const historyStr = JSON.stringify(operationData.status.history);

      //Looking for a row like this: "message": "{\"requested\": 3, \"succeeded\": 2}"
      const requestedPos: number = historyStr.lastIndexOf(`"requested\\":`);
      const firstComma = historyStr.indexOf(',', requestedPos);
      const succeededPos: number = historyStr.lastIndexOf(`"succeeded\\":`);
      if (succeededPos < 0){
        //In case for some strange reason operation is SUCCEEDED but no 'succeeded' element was found
        operationData.status.value = BULK_OPERATION_STATUS.BULK_FAILED;
      }
      const secondComma = historyStr.indexOf(',', succeededPos);

      const requestedCount: string = historyStr.substring(requestedPos, firstComma).match(/\d+/)[0].trim(); //Getting the number
      const successCount: string = historyStr.substring(succeededPos, secondComma).match(/\d+/)[0].trim();

      if (requestedCount != successCount){
        operationData.status.value = BULK_OPERATION_STATUS.BULK_PARTLY_SUCCESS;
      }
    }
  }  
}
